/**
 * Gyroscope/accelerometer tilt controls for Pac-Man movement.
 * Calibrates the neutral position on game start so the current
 * device orientation becomes the "resting" position.
 */

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

/** Minimum tilt angle (degrees) to register a directional input. */
const TILT_THRESHOLD_DEG = 12;

/** DeviceOrientation calibration baseline. */
interface CalibrationData {
  readonly beta: number;  // front-to-back tilt (-180 to 180)
  readonly gamma: number; // left-to-right tilt (-90 to 90)
}

export class TiltInput {
  private currentDirection: Direction = 'none';
  private calibration: CalibrationData | null = null;
  private isAvailable = false;
  private hasReceivedEvent = false;
  private boundHandler: ((event: DeviceOrientationEvent) => void) | null = null;

  constructor() {
    this.checkAvailability();
  }

  /** Returns true if the device supports gyroscope/accelerometer. */
  getIsAvailable(): boolean {
    return this.isAvailable;
  }

  /** Returns true once a real orientation event has been received. */
  getHasReceivedEvent(): boolean {
    return this.hasReceivedEvent;
  }

  getDirection(): Direction {
    return this.currentDirection;
  }

  /** Start listening for orientation events and calibrate. */
  start(): void {
    if (this.boundHandler) {
      return; // Already started
    }

    this.boundHandler = (event: DeviceOrientationEvent) => {
      this.handleOrientation(event);
    };

    window.addEventListener('deviceorientation', this.boundHandler, true);
  }

  /** Recalibrate the neutral position to the current device orientation. */
  calibrate(): void {
    // Reset calibration so next event sets the baseline
    this.calibration = null;
  }

  /** Stop listening for orientation events. */
  stop(): void {
    if (this.boundHandler) {
      window.removeEventListener('deviceorientation', this.boundHandler, true);
      this.boundHandler = null;
    }
    this.currentDirection = 'none';
  }

  destroy(): void {
    this.stop();
  }

  private checkAvailability(): void {
    // DeviceOrientationEvent exists in most browsers but only fires on
    // devices with an actual gyroscope/accelerometer.
    this.isAvailable = typeof DeviceOrientationEvent !== 'undefined';

    // On iOS 13+ we may need to request permission
    const doe = DeviceOrientationEvent as unknown as Record<string, unknown>;
    if (this.isAvailable && typeof doe['requestPermission'] === 'function') {
      this.requestiOSPermission(doe['requestPermission'] as () => Promise<string>);
    }
  }

  private requestiOSPermission(requestFn: () => Promise<string>): void {
    requestFn()
      .then((state: string) => {
        this.isAvailable = state === 'granted';
      })
      .catch(() => {
        this.isAvailable = false;
      });
  }

  private handleOrientation(event: DeviceOrientationEvent): void {
    const beta = event.beta;   // front-to-back tilt
    const gamma = event.gamma; // left-to-right tilt

    if (beta === null || gamma === null) {
      return;
    }

    this.hasReceivedEvent = true;

    // Calibrate on first event if not yet calibrated
    if (!this.calibration) {
      this.calibration = { beta, gamma };
      return;
    }

    // Calculate relative tilt from calibration baseline
    const relativeBeta = beta - this.calibration.beta;
    const relativeGamma = gamma - this.calibration.gamma;

    this.currentDirection = this.anglesToDirection(relativeBeta, relativeGamma);
  }

  private anglesToDirection(beta: number, gamma: number): Direction {
    const absBeta = Math.abs(beta);
    const absGamma = Math.abs(gamma);

    // Neither axis exceeds threshold — no input
    if (absBeta < TILT_THRESHOLD_DEG && absGamma < TILT_THRESHOLD_DEG) {
      return 'none';
    }

    // Choose the dominant axis
    if (absGamma >= absBeta) {
      // Left-right tilt dominates
      return gamma > 0 ? 'right' : 'left';
    }
    // Front-back tilt dominates
    // In landscape: positive beta = device top tilts toward user (lean back)
    // Tilt forward/away from user = negative beta = move up
    // Tilt backward/toward user = positive beta = move down
    return beta > 0 ? 'down' : 'up';
  }
}
